<script setup lang="ts">
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { CardAspect } from '../card.constants';
import Icon from '../../icon/icon.vue';
import { IconName, IconSize } from '../../icon/icon.constants';
import { useLink } from '../../../composables/use-link';

type Props = {
  /** Image URL. When omitted the fallback illustration is rendered instead. */
  src?: string | null;
  /** Accessible description of the image. */
  alt?: string;
  /** Shape of the media box. */
  aspect?: CardAspect;
  /** Glyph shown in place of a missing image. */
  fallbackIconName?: IconName;
  /**
   * Router target for the image. When set, the media renders as the host app's
   * link component (NuxtLink or RouterLink). Takes precedence over `href`. The
   * overlay slots stay outside the link so their controls remain clickable.
   */
  to?: RouteLocationRaw;
  /** External/plain URL. When set (and `to` is not), the media renders as an `<a>`. */
  href?: string;
};

const props = withDefaults(defineProps<Props>(), {
  aspect: CardAspect.RATIO_3_2,
  alt: '',
  fallbackIconName: IconName.FILE_IMAGE,
});

const linkComponent = useLink();

// Binding `href` alongside `to` — even as `undefined` — overwrites the href a
// router link generates, leaving a dead anchor. Only ever bind one of them.
const linkProps = computed(() => {
  if (props.to !== undefined) {
    return { to: props.to };
  }

  if (props.href !== undefined) {
    return { href: props.href };
  }

  return {};
});

const mediaTag = computed(() => {
  if (props.to !== undefined) {
    return linkComponent;
  }

  if (props.href !== undefined) {
    return 'a';
  }

  return 'div';
});

const aspectClass: Record<CardAspect, string> = {
  [CardAspect.RATIO_3_2]: 'aspect-[3/2] rounded-sm',
  [CardAspect.RATIO_4_3]: 'aspect-[4/3] rounded-sm',
  [CardAspect.RATIO_16_9]: 'aspect-[16/9] rounded-sm',
  [CardAspect.CIRCLE]: 'aspect-square rounded-full',
};
</script>

<template>
  <div
    class="relative w-full shrink-0 overflow-hidden bg-primary-50"
    :class="aspectClass[props.aspect]"
  >
    <component :is="mediaTag" v-bind="linkProps" class="block size-full">
      <img
        v-if="props.src"
        :src="props.src"
        :alt="props.alt"
        class="size-full object-cover"
      />
      <span v-else class="flex size-full items-center justify-center">
        <slot name="fallback">
          <Icon
            :name="props.fallbackIconName"
            :size="IconSize.XL"
            class="text-primary"
          />
        </slot>
      </span>
    </component>

    <div class="absolute top-[0.25rem] left-[0.25rem]">
      <slot name="top-left" />
    </div>
    <div class="absolute top-md right-md flex flex-col items-end gap-sm">
      <slot name="top-right" />
    </div>
    <div class="absolute right-md bottom-md flex items-center gap-sm">
      <slot name="bottom-right" />
    </div>
  </div>
</template>
